/* requires:
polyfill.js
*/

const paginationObject = {
  itemsPerPage: 10,
  currentPage: 1,
  articleItems: Array.from(document.querySelectorAll('#article-container > article')),

  init() {
    const totalPages = this.getTotalPages();
    const pageFromQuery = this.getPageFromQuery();
    this.currentPage = this.normalizePageNumber(pageFromQuery, totalPages);
    this.render();
    this.bindResizeHandler();
  },

  // Parse ?page= from the current URL and return a safe numeric page.
  getPageFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const rawValue = Number(params.get('page'));
    return Number.isFinite(rawValue) && rawValue > 0 ? rawValue : 1;
  },

  getTotalPages() {
    return Math.max(1, Math.ceil(this.articleItems.length / this.itemsPerPage));
  },

  // function to keep the page number within the valid range, prevents out of bounds errors
  normalizePageNumber(page, totalPages = this.getTotalPages()) {
    return Math.min(Math.max(page, 1), totalPages);
  },

  // function to render the visible items and the pagination navigation
  render() {
    this.renderVisibleItems();
    this.renderPaginationNavigation();
    this.updateSeoLinks();
  },

  // function to render the visible items
  renderVisibleItems() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;

    this.articleItems.forEach((item, index) => {
      item.classList.toggle('d-none', index < startIndex || index >= endIndex);
    });
  },

  // function to render the pagination navigation
  renderPaginationNavigation() {
    const totalPages = this.getTotalPages();
    const articleContainer = document.querySelector('#article-container');

    if (!articleContainer) {
      return;
    }

    const existingNav = articleContainer.querySelector('.location-pagination');
    if (existingNav) {
      existingNav.remove();
    }

    if (totalPages <= 1) {
      return;
    }

    const numberButtonsHtml = this.buildNumberButtonsHtml(totalPages);
    const isOnFirstPage = this.currentPage === 1;
    const isOnLastPage = this.currentPage === totalPages;
    const previousPageHref = this.buildPageUrl(this.normalizePageNumber(this.currentPage - 1, totalPages));
    const nextPageHref = this.buildPageUrl(this.normalizePageNumber(this.currentPage + 1, totalPages));

    const paginationHtml = `
      <nav class="location-pagination mt-4">
        <ul class="pagination pagination-sm justify-content-center">
          <li class="page-item">
            ${
              isOnFirstPage
                ? `<span class="page-link py-2 px-3 text-dark fs-5 me-1 rounded-1 bg-btn-disabled" aria-label="Previous" aria-disabled="true"><span aria-hidden="true">&laquo;</span></span>`
                : `<a href="${previousPageHref}" class="page-link py-2 px-3 text-dark fs-5 me-1 rounded-1" aria-label="Previous" data-page-action="prev"><span aria-hidden="true">&laquo;</span></a>`
            }
          </li>
          ${numberButtonsHtml}
          <li class="page-item">
            ${
              isOnLastPage
                ? `<span class="page-link py-2 px-3 text-dark fs-5 ms-1 rounded-1 bg-btn-disabled" aria-label="Next" aria-disabled="true"><span aria-hidden="true">&raquo;</span></span>`
                : `<a href="${nextPageHref}" class="page-link py-2 px-3 text-dark fs-5 ms-1 rounded-1" aria-label="Next" data-page-action="next"><span aria-hidden="true">&raquo;</span></a>`
            }
          </li>
        </ul>
      </nav>
    `;

    articleContainer.insertAdjacentHTML('beforeend', paginationHtml);
    this.bindPaginationButtonEvents();
  },

  // function to get the maximum number of visible number buttons
  getMaxVisibleNumberButtons() {
    if (window.matchMedia('(max-width: 576px)').matches) {
      return 3;
    }
    if (window.matchMedia('(max-width: 992px)').matches) {
      return 5;
    }
    return 9;
  },

  // function to get the visible page range
  getVisiblePageRange(totalPages) {
    const maxVisible = this.getMaxVisibleNumberButtons();

    if (totalPages <= maxVisible) {
      return { startPage: 1, endPage: totalPages };
    }

    const halfWindow = Math.floor(maxVisible / 2);
    let startPage = this.currentPage - halfWindow;
    let endPage = this.currentPage + halfWindow;

    if (startPage < 1) {
      startPage = 1;
      endPage = maxVisible;
    }

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - maxVisible + 1;
    }

    return { startPage, endPage };
  },

  // function to build the number buttons HTML
  buildNumberButtonsHtml(totalPages) {
    const { startPage, endPage } = this.getVisiblePageRange(totalPages);
    let html = '';

    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
      const isActive = pageNumber === this.currentPage ? ' active' : '';
      const isCurrentPage = pageNumber === this.currentPage;
      html += `
        <li class="page-item">
          ${
            isCurrentPage
              ? `<span class="page-link page-numbers py-2 px-3 text-dark fs-5 mx-1 rounded-1 active" aria-current="page">${pageNumber}</span>`
              : `<a href="${this.buildPageUrl(
                  pageNumber
                )}" class="page-link page-numbers py-2 px-3 text-dark fs-5 mx-1 rounded-1${isActive}" data-page-number="${pageNumber}">${pageNumber}</a>`
          }
        </li>
      `;
    }

    return html;
  },

  buildPageUrl(pageNumber) {
    const safePage = this.normalizePageNumber(pageNumber);
    const pathname = this.stripTrailingSlash(window.location.pathname);
    return safePage === 1 ? pathname : `${pathname}?page=${safePage}`;
  },

  bindPaginationButtonEvents() {
    const previousLink = document.querySelector('[data-page-action="prev"]');
    const nextLink = document.querySelector('[data-page-action="next"]');
    const pageNumberLinks = document.querySelectorAll('a[data-page-number]');

    pageNumberLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const nextPage = Number(link.dataset.pageNumber);
        this.goToPage(nextPage);
      });
    });

    if (previousLink) {
      previousLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.goToPage(this.currentPage - 1);
      });
    }

    if (nextLink) {
      nextLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.goToPage(this.currentPage + 1);
      });
    }
  },

  goToPage(nextPage) {
    const safePage = this.normalizePageNumber(nextPage);
    this.currentPage = safePage;
    this.syncHistoryUrl();
    this.render();
    this.scrollToResultsTop();
  },

  // Keep page 1 clean (/blog) and only add ?page=n for pages > 1.
  syncHistoryUrl() {
    const pathname = this.stripTrailingSlash(window.location.pathname);
    const newPath = this.currentPage === 1 ? pathname : `${pathname}?page=${this.currentPage}`;
    history.pushState(null, '', newPath);
  },

  // Utility to remove trailing slash from a path
  stripTrailingSlash(path) {
    return path.endsWith('/') ? path.slice(0, -1) : path;
  },

  // Keep SEO links aligned with the currently visible page.
  updateSeoLinks() {
    const totalPages = this.getTotalPages();
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousLink = document.querySelector('link[rel="prev"]');
    const nextLink = document.querySelector('link[rel="next"]');

    if (!canonicalLink) {
      return;
    }

    const canonicalBase = this.stripTrailingSlash(canonicalLink.getAttribute('href').split('?')[0]);

    if (this.currentPage === 1) {
      canonicalLink.setAttribute('href', canonicalBase);
    } else {
      canonicalLink.setAttribute('href', `${canonicalBase}?page=${this.currentPage}`);
    }

    if (previousLink) {
      if (this.currentPage === 1) {
        previousLink.setAttribute('href', '');
      } else if (this.currentPage === 2) {
        previousLink.setAttribute('href', canonicalBase);
      } else {
        previousLink.setAttribute('href', `${canonicalBase}?page=${this.currentPage - 1}`);
      }
    }

    if (nextLink) {
      if (this.currentPage === totalPages) {
        nextLink.setAttribute('href', '');
      } else {
        nextLink.setAttribute('href', `${canonicalBase}?page=${this.currentPage + 1}`);
      }
    }
  },

  scrollToResultsTop() {
    const scrollTarget = document.querySelector('#scrollTo');
    if (scrollTarget) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },

  bindResizeHandler() {
    window.addEventListener('resize', () => {
      if (this.getTotalPages() > 1) {
        this.renderPaginationNavigation();
      }
    });
  },
};

paginationObject.init();
